import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader, StatusBadge, Empty } from "@/components/admin/common";
import { ClientFormDialog } from "@/components/admin/ClientFormDialog";
import { api, formatErr } from "@/lib/api";

const FILTERS = [["active", "Attivi"], ["archived", "Archiviati"], ["all", "Tutti"]];

export default function Clients() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [clients, setClients] = useState(null);
  const [form, setForm] = useState({ open: false, client: null });

  const load = () => api.get("/clients", { params: { q, status } }).then((r) => setClients(r.data));
  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const toggleArchive = (c) => act(() => api.put(`/clients/${c.id}`, { ...c, status: c.status === "archived" ? "active" : "archived" }), c.status === "archived" ? "Cliente ripristinato" : "Cliente archiviato");

  return (
    <div>
      <PageHeader eyebrow="Anagrafica" title="Clienti" description="Ogni cliente può avere una o più digital card.">
        <Button onClick={() => setForm({ open: true, client: null })} data-testid="add-client-button"><Plus className="mr-1.5 h-4 w-4" />Nuovo cliente</Button>
      </PageHeader>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" /><Input data-testid="client-search-input" placeholder="Cerca per nome, azienda o email" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 bg-white pl-9" /></div>
        <div className="flex rounded-lg border bg-white p-1">
          {FILTERS.map(([k, l]) => <button key={k} data-testid={`client-filter-${k}`} onClick={() => setStatus(k)} className={`rounded-md px-3 py-1.5 text-xs ${status === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>)}
        </div>
      </div>
      {clients && !clients.length ? (
        <Empty title="Nessun cliente">Aggiungi il primo cliente per creare la sua digital card.</Empty>
      ) : (
        <div className="panel overflow-x-auto" data-testid="clients-table">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b text-left">{["Cliente", "Contatto", "Card", "Visite", "Stato", ""].map((h) => <th key={h} className="eyebrow px-5 py-3 font-normal">{h}</th>)}</tr></thead>
            <tbody>
              {(clients || []).map((c) => (
                <tr key={c.id} className="cursor-pointer border-b last:border-0 transition-colors hover:bg-zinc-50/70" onClick={() => nav(`/admin/clienti/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3.5"><div className="font-medium">{c.name}</div><div className="text-xs text-zinc-500">{c.company}</div></td>
                  <td className="px-5 py-3.5 text-zinc-600">{c.email || "—"}</td>
                  <td className="px-5 py-3.5 tabular-nums">{c.card_count}<span className="text-zinc-400"> · {c.published_count} online</span></td>
                  <td className="px-5 py-3.5 tabular-nums">{c.views}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={c.status} testId={`client-status-${c.id}`} /></td>
                  <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="rounded-md p-2 text-zinc-500 hover:bg-zinc-100" data-testid={`client-menu-${c.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => nav(`/admin/clienti/${c.id}`)}>Apri</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setForm({ open: true, client: c })} data-testid={`client-edit-${c.id}`}>Modifica</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => act(() => api.post(`/clients/${c.id}/duplicate`), "Cliente duplicato (con le sue card in bozza)")} data-testid={`client-duplicate-${c.id}`}>Duplica</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleArchive(c)} data-testid={`client-archive-${c.id}`}>{c.status === "archived" ? "Ripristina" : "Archivia"}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClientFormDialog open={form.open} client={form.client} onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))} onSaved={(c) => (form.client ? load() : nav(`/admin/clienti/${c.id}`))} />
    </div>
  );
}
